import {
  freezeCard,
  getCardLimit,
  getCardSecrets,
  getCardStatus,
  getUserCards,
  updateLimit,
  updatePin,
} from "../api/api";
import { generateCardOperationSign } from "../api/auth";
import { useSDKConfigStore } from "../stores/sdkConfigStore";
import { useCardStatusStore } from "../stores/cardStatusStore";
import { encryptPinWithSessionKey } from "../api/utils";
import { CardLimitFrequency } from "../api/types";

/**
 * Handles freezing or unfreezing a card by generating an EIP-712 signature and calling the backend API.
 *
 * @param isFreezing - Boolean flag to indicate whether to freeze (`true`) or unfreeze (`false`) the card.
 * @param chainId - The blockchain network's chain ID.
 * @param signTypedData - A signer function that returns a signature for the request using EIP-712.
 * @returns An object indicating success or failure with a descriptive message.
 */
export const handleFreezeCard = async ({
  isFreezing,
  chainId,
  signTypedData,
}: {
  isFreezing: boolean;
  chainId: number;
  signTypedData: (typedData: any) => Promise<string>;
}): Promise<{
  success: boolean;
  message: string;
}> => {
  try {
    console.log("[handleFreezeCard] Starting card freeze/unfreeze process...");

    const { eoaAddress } = useSDKConfigStore.getState();
    const { userCard, _setCardStatus } = useCardStatusStore.getState();

    if (!eoaAddress || !userCard) {
      console.warn(
        "[handleFreezeCard] Wallet address or card data is missing."
      );
      return {
        success: false,
        message: "Wallet address or card data is missing.",
      };
    }

    const eoa = eoaAddress as `0x${string}`;
    const cardId = userCard.cardID;
    const timestamp = Math.floor(Date.now() / 1000);
    const action = isFreezing ? "freeze" : "unfreeze";
    const pastTense = isFreezing ? "frozen" : "unfrozen";

    console.log(
      `[handleFreezeCard] Signing ${action} request for card ID: ${cardId}`
    );

    const { signature } = await generateCardOperationSign(
      eoa,
      chainId,
      cardId,
      "updateCardStatus",
      timestamp,
      signTypedData
    );

    console.log("[handleFreezeCard] Signature generated:", signature);

    const { success, message } = await freezeCard(
      eoa,
      cardId,
      chainId,
      signature,
      timestamp,
      isFreezing,
      "updateCardStatus"
    );

    if (success) {
      console.log(`[handleFreezeCard] Card successfully ${pastTense}.`);

      const [cardStatusResponse, userCardsResponse] = await Promise.all([
        getCardStatus(eoaAddress),
        getUserCards(eoaAddress),
      ]);

      const updatedUserCard = userCardsResponse.data?.[0] || null;
      _setCardStatus(cardStatusResponse.data, updatedUserCard);
    } else {
      console.warn(`[handleFreezeCard] Failed to ${action} card: ${message}`);
    }

    return { success, message: message || "" };
  } catch (err) {
    const message =
      err instanceof Error ? err.message : "An unexpected error occurred.";
    console.error(
      `[handleFreezeCard] Error during ${
        isFreezing ? "freeze" : "unfreeze"
      } operation:`,
      err
    );
    return { success: false, message };
  }
};

/**
 * Handles the retrieval of encrypted card details using session-based authentication and EIP-712 signing.
 *
 * @param chainId - The blockchain network's chain ID.
 * @param signTypedData - A signer function that returns a signature for the request using EIP-712.
 * @param sessionID - The session ID used to authorize the request (usually from Rain).
 * @returns An object indicating success or failure, with a message and encrypted card data (if successful).
 */
export const handleShowCardDetails = async ({
  chainId,
  signTypedData,
  sessionID,
}: {
  chainId: number;
  signTypedData: (typedData: any) => Promise<string>;
  sessionID: string;
}): Promise<{
  success: boolean;
  message: string;
  secrets?: {
    encryptedPan: { iv: string; data: string };
    encryptedCvc: { iv: string; data: string };
    ExpirationMonth: string;
    ExpirationYear: string;
  };
}> => {
  try {
    console.log("[handleViewCardSecrets] Starting card secrets retrieval...");

    const { eoaAddress } = useSDKConfigStore.getState();
    const { userCard } = useCardStatusStore.getState();

    if (!eoaAddress || !userCard) {
      console.warn(
        "[handleViewCardSecrets] Wallet address or card data is missing."
      );
      return {
        success: false,
        message: "Wallet address or card information is missing.",
      };
    }

    const eoa = eoaAddress as `0x${string}`;
    const cardID = userCard.cardID;
    const timestamp = Math.floor(Date.now() / 1000);

    console.log(
      `[handleViewCardSecrets] Signing "view" request for card ID: ${cardID}`
    );

    const { signature } = await generateCardOperationSign(
      eoa,
      chainId,
      cardID,
      "getEncryptedCardDetails",
      timestamp,
      signTypedData
    );

    const secrets = await getCardSecrets(
      eoa,
      cardID,
      chainId,
      sessionID,
      signature,
      timestamp,
      "getEncryptedCardDetails"
    );

    console.log("[handleViewCardSecrets] Card secrets retrieved successfully.");

    return {
      success: true,
      message: "Card secrets fetched successfully.",
      secrets,
    };
  } catch (err) {
    const message =
      err instanceof Error ? err.message : "An unexpected error occurred.";
    console.error(
      "[handleViewCardSecrets] Error retrieving card secrets:",
      err
    );
    return {
      success: false,
      message,
    };
  }
};

/**
 * Handles PIN encryption and secure update for a given card using a session-based EIP-712 flow.
 *
 * @param pin - The plain-text PIN entered by the user.
 * @param chainId - The blockchain network's chain ID.
 * @param signTypedData - A signer function that returns a signature for the request.
 * @param sessionID - The session ID (generated via Rain).
 * @param secretKey - Hex-encoded session secret key used for AES-GCM encryption.
 * @returns An object indicating success or failure with a message.
 */
export const handleUpdatePin = async ({
  pin,
  chainId,
  signTypedData,
  sessionID,
  secretKey,
}: {
  pin: string;
  chainId: number;
  signTypedData: (typedData: any) => Promise<string>;
  sessionID: string;
  secretKey: string;
}): Promise<{ success: boolean; message: string }> => {
  try {
    console.log("[handleUpdatePin] Starting PIN update process...");

    const { eoaAddress } = useSDKConfigStore.getState();
    const { userCard } = useCardStatusStore.getState();

    if (!eoaAddress || !userCard) {
      console.warn("[handleUpdatePin] Wallet address or card data is missing.");
      return {
        success: false,
        message: "Wallet address or card data is missing.",
      };
    }

    const eoa = eoaAddress as `0x${string}`;
    const cardId = userCard.cardID;
    const timestamp = Math.floor(Date.now() / 1000);
    const action = "updateCardPin";

    console.log(
      `[handleUpdatePin] Signing ${action} request for card ID: ${cardId}`
    );

    const { signature } = await generateCardOperationSign(
      eoa,
      chainId,
      cardId,
      "updateCardPin",
      timestamp,
      signTypedData
    );

    console.log("[handleUpdatePin] Signature generated. Encrypting PIN...");

    const { iv, data } = encryptPinWithSessionKey(pin, secretKey);

    const { success, message } = await updatePin(
      eoa,
      cardId,
      chainId,
      sessionID,
      signature,
      timestamp,
      iv,
      data,
      action
    );

    if (success) {
      console.log("[handleUpdatePin] PIN successfully updated.");
    } else {
      console.warn("[handleUpdatePin] Failed to update PIN:", message);
    }

    return { success, message: message || "" };
  } catch (err) {
    const message =
      err instanceof Error ? err.message : "An unexpected error occurred.";
    console.error("[handleUpdatePin] Error during PIN update operation:", err);
    return { success: false, message };
  }
};

/**
 * Handles showing the card limit by fetching it from the backend API.
 *
 * @returns An object indicating success or failure with a message and the card limits (if successful).
 */
export const handleShowCardLimit = async (): Promise<{
  success: boolean;
  message: string;
  limits?: {
    amount: number; // in cents
    frequency: CardLimitFrequency;
  };
}> => {
  try {
    console.log("[handleShowCardLimit] Starting card limit retrieval...");

    const { eoaAddress } = useSDKConfigStore.getState();
    const { userCard } = useCardStatusStore.getState();

    if (!eoaAddress || !userCard) {
      console.warn(
        "[handleShowCardLimit] Wallet address or card data is missing."
      );
      return {
        success: false,
        message: "Wallet address or card information is missing.",
      };
    }

    const cardID = userCard.cardID;

    console.log(
      `[handleShowCardLimit] Signing "view" request for card ID: ${cardID}`
    );

    const { amount, frequency } = await getCardLimit(cardID);

    console.log("[handleShowCardLimit] Card limit retrieved successfully.");

    return {
      success: true,
      message: "Card limit fetched successfully.",
      limits: {
        amount, // in cents
        frequency, // e.g. "per24HourPeriod"
      },
    };
  } catch (err) {
    const message =
      err instanceof Error ? err.message : "An unexpected error occurred.";
    console.error("[handleShowCardLimit] Error retrieving card limit:", err);
    return {
      success: false,
      message,
    };
  }
};

/**
 * Handles updating the transaction limit for a card by generating an EIP-712 signature and calling the backend API.
 *
 * @param limit - The new transaction limit to set, as a string (e.g. "100.00").
 * @param chainId - The blockchain network's chain ID.
 * @param signTypedData - A signer function that returns a signature for the request using EIP-712.
 * @returns An object indicating success or failure with a descriptive message.
 */
export const handleUpdateLimit = async ({
  limit,
  chainId,
  signTypedData,
}: {
  limit: string;
  chainId: number;
  signTypedData: (typedData: any) => Promise<string>;
}): Promise<{ success: boolean; message: string }> => {
  try {
    console.log("[handleUpdateLimit] Starting Limit update process...");

    const { eoaAddress } = useSDKConfigStore.getState();
    const { userCard } = useCardStatusStore.getState();

    if (!eoaAddress || !userCard) {
      console.warn(
        "[handleUpdateLimit] Wallet address or card data is missing."
      );
      return {
        success: false,
        message: "Wallet address or card data is missing.",
      };
    }

    const eoa = eoaAddress as `0x${string}`;
    const cardId = userCard.cardID;
    const timestamp = Math.floor(Date.now() / 1000);
    const action = "updateCardLimits";
    const formattedLimit = Math.round(parseFloat(limit) * 100);

    console.log(
      `[handleUpdateLimit] Signing ${action} request for card ID: ${cardId}`
    );

    const { signature } = await generateCardOperationSign(
      eoa,
      chainId,
      cardId,
      action,
      timestamp,
      signTypedData
    );

    console.log("[handleUpdateLimit] Signature generated. Encrypting PIN...");

    const { success, message } = await updateLimit(
      eoa,
      cardId,
      chainId,
      signature,
      timestamp,
      formattedLimit,
      action
    );

    if (success) {
      console.log("[handleUpdateLimit] limit successfully updated.");
    } else {
      console.warn("[handleUpdateLimit] Failed to update limit:", message);
    }

    return { success, message: message || "" };
  } catch (err) {
    const message =
      err instanceof Error ? err.message : "An unexpected error occurred.";
    console.error(
      "[handleUpdateLimit] Error during limit update operation:",
      err
    );
    return { success: false, message };
  }
};
